import React, { useEffect } from 'react';
import './tariff.scss';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import PageHeader from '../../../components/base/PageHeader/PageHeader';
import {
  Button, Col, Row,
} from '../../../components/base/BootstrapComponents';
import { getTariffData } from '../reducer';
import notificationDecorator from '../../Notifications/notificationDecorator';
import { responseLogWithNotification } from '../../../utils/utils';
import {
  createLostTicket,
  deleteLostTicket,
  editLostTicket,
  loadLostTicketList,
} from '../actions';
import FieldDTO from '../../../components/fields/Abstract/FieldDTO';
import TextInputWithLabel from '../../../components/fields/TextInput/TextInputWithLabel';
import Loader from '../../../components/base/Loader/Loader';
import Switches from '../../../components/fields/Switches/Switches';
import { showGlobalConfirm } from '../../System/actions';

const defaultValues = {
  name: 'Штрафной билет',
  description: '',
  cost: '',
  is_active: true,
};

const attributes = {
  name: {
    name: 'name',
    type: String,
    validators: {},
    labelKey: 'lost.ticket.name',
  },
  cost: {
    name: 'cost',
    type: Number,
    validators: {},
    labelKey: 'lost.ticket.cost',
  },
  description: {
    name: 'description',
    type: String,
    validators: {},
    labelKey: 'lost.ticket.description',
  },
  is_active: {
    name: 'is_active',
    type: Boolean,
    validators: {},
    labelKey: 'lost.ticket.is_active',
  },
};

const normalizeCost = value => {
  if (typeof value === 'number') {
    return value;
  }

  const rawValue = String(value || '').replace(',', '.').trim();
  if (!rawValue) {
    return 0;
  }

  const parsed = Number(rawValue);
  return Number.isFinite(parsed) ? parsed : null;
};

const normalizePayload = (data, cost) => ({
  ...data,
  name: String(data?.name || defaultValues.name).trim() || defaultValues.name,
  description: data?.description || null,
  cost,
  is_active: Boolean(data?.is_active),
});

export default function LostTicketPage(props) {
  const {
    handleClose = null,
    closeOnSave = false,
    onSaved = () => {},
  } = props;
  const dispatch = useDispatch();
  const {
    lostTicketList,
    loadingLostTicketList,
    loadingCreateLostTicket,
    loadingEditLostTicket,
    loadingDeleteLostTicket,
  } = useSelector(getTariffData);
  const lostTicket = Array.isArray(lostTicketList) && lostTicketList.length ? lostTicketList[0] : null;

  const {
    control, getValues, reset,
  } = useForm({ defaultValues });

  const loadData = () => dispatch(loadLostTicketList({ all: true })).catch(responseLogWithNotification);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    reset(lostTicket?.id ? {
      ...defaultValues,
      ...lostTicket,
    } : defaultValues);
  }, [lostTicket, reset]);

  const handleSave = () => {
    const isUpdate = Boolean(lostTicket?.id);
    const formData = {
      ...getValues(),
      id: lostTicket?.id,
    };
    const cost = normalizeCost(formData.cost);

    if (cost === null) {
      notificationDecorator({
        title: 'Штрафной билет',
        message: 'Укажите корректную стоимость',
        isError: true,
      });
      return;
    }

    const data = normalizePayload(formData, cost);
    const action = isUpdate ? editLostTicket : createLostTicket;

    dispatch(action(data))
      .then(() => {
        loadData();
        onSaved();
        notificationDecorator({
          title: isUpdate ? 'Редактирование штрафного билета' : 'Создание штрафного билета',
          message: isUpdate ? 'Редактирование штрафного билета прошло успешно' : 'Создание штрафного билета прошло успешно',
        });
        if (closeOnSave && handleClose) {
          handleClose();
        }
      })
      .catch(responseLogWithNotification);
  };

  const handleDelete = () => {
    if (!lostTicket?.id) {
      return;
    }

    dispatch(showGlobalConfirm({
      confirmTitle: 'Удаление штрафного билета',
      confirmMessage: 'Вы уверены, что хотите удалить настройку штрафного билета?',
      confirmButtonText: 'Удалить',
    })).then(() => {
      dispatch(deleteLostTicket(lostTicket.id))
        .then(() => {
          reset(defaultValues);
          loadData();
          notificationDecorator({
            title: 'Удаление штрафного билета',
            message: 'Успешно',
          });
          if (handleClose) {
            handleClose();
          }
        })
        .catch(responseLogWithNotification);
    }).catch(() => {});
  };

  return (
    <div className="tariff-tab">
      <Loader global loading={loadingLostTicketList || loadingCreateLostTicket || loadingEditLostTicket || loadingDeleteLostTicket} />
      <div className="page-container tariff-form">
        <div className="inputs-box">
          <PageHeader header="Штрафной билет" />
        </div>
        <div className="plp-table g-0">
          <div className="frame py-3 d-flex flex-column px-3">
            <div className="d-flex flex-row justify-content-between align-items-center">
              <div className="h4">
                Данные штрафного билета
              </div>
            </div>
            <Row className="h-100">
              <Col columns={6}>
                <FieldDTO
                  control={control}
                  type="text"
                  className="mb-2"
                  dtoObject={attributes.name}
                  addPlaceholder
                  component={TextInputWithLabel}
                />
                <FieldDTO
                  control={control}
                  type="text"
                  className="mb-2"
                  dtoObject={attributes.cost}
                  addPlaceholder
                  component={TextInputWithLabel}
                />
                <FieldDTO
                  control={control}
                  type="text"
                  className="mb-2"
                  dtoObject={attributes.description}
                  addPlaceholder
                  component={TextInputWithLabel}
                  textarea
                />
                <FieldDTO
                  control={control}
                  type="text"
                  className="mb-2"
                  dtoObject={attributes.is_active}
                  addPlaceholder
                  component={Switches}
                />
                <div className="text-center justify-content-center p-3">
                  <Button className="me-3 px-3" onClick={handleSave}>Сохранить</Button>
                  {lostTicket?.id
                    ? <Button variant="danger" className="me-3 px-3" onClick={handleDelete}>Удалить</Button>
                    : null}
                  {handleClose
                    ? <Button variant="secondary" className="px-3" onClick={handleClose}>Закрыть</Button>
                    : null}
                </div>
              </Col>
            </Row>
          </div>
        </div>
      </div>
    </div>
  );
}
